import {
  deleteReapproCategory,
  useReapproCategories,
  useReapproSimulationAllStoresEmail,
} from '@gozoki/api'
import {
  Calculator,
  Check,
  Eye,
  FileSpreadsheet,
  Pencil,
  Plus,
  Search,
  Settings,
  Trash2,
  Upload,
  X,
} from 'lucide-react'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog'

import { includesLowerCase } from '@gozoki/tools'
import { useMutation } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { AxiosError } from 'axios'
import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '~/components/ui/button'
import { useRights } from '../../../utils/hooks'
import { FormulaCard } from './FormulaCard'
import { ReapproCategoryDialog } from './ReapproCategoryDialog'
import { ReapproNeedBulkImportDialog } from './ReapproNeedBulkImportDialog'
import { ReapproSimulationEmailDialog } from './ReapproSimulationEmailDialog'
import { ReapproStockMinBulkDialog } from './ReapproStockMinBulkDialog'

const formatHour = (value: string) => value.split(':').slice(0, 2).join(':')
const formatCoverageRate = (value: number) => Number.parseFloat(Number(value).toFixed(2)).toString()

export const ReapproCategoriesPanel = () => {
  const isUserAdmin = useRights(['ADMIN'])

  const navigate = useNavigate()

  const [search, setSearch] = useState('')
  const [selectedCategories, setSelectedCategories] = useState<number[]>([])
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [simulationDialogOpen, setSimulationDialogOpen] = useState(false)
  const [stockMinDialogOpen, setStockMinDialogOpen] = useState(false)
  const [reapproImportDialogOpen, setReapproImportDialogOpen] = useState(false)

  const { data: reapproCategories = [], refetch, isLoading, isPending } = useReapproCategories()

  const filteredCategories = reapproCategories.filter((category) =>
    includesLowerCase(category.name, search),
  )

  const deleteCategoryMut = useMutation({ mutationFn: deleteReapproCategory })
  const { mutate: requestSimulationAllStoresByEmail, isPending: isRequestingSimulationAllStores } =
    useReapproSimulationAllStoresEmail()

  const handleDeleteCategory = async () => {
    try {
      await deleteCategoryMut.mutateAsync(selectedCategories)
      setSelectedCategories([])
      refetch()
    } catch (error) {
      let message = 'Une erreur inconnue est survenue lors de la suppression de la catégorie'
      if (
        error instanceof AxiosError &&
        error.response?.data &&
        typeof error.response.data === 'object' &&
        'message' in error.response.data
      ) {
        message = error.response.data.message as string
      }
      toast.error(message)
    } finally {
      setDeleteConfirmOpen(false)
    }
  }

  const toggleSelectAll = () => {
    if (selectedCategories.length === filteredCategories.length) {
      setSelectedCategories([])
    } else {
      setSelectedCategories(filteredCategories.map((c) => c.id))
    }
  }

  const toggleSelectCategory = (id: number) => {
    setSelectedCategories((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id],
    )
  }

  const deleteDisabled = selectedCategories.length === 0 || deleteCategoryMut.isPending
  const modifyDisabled = selectedCategories.length !== 1 || deleteCategoryMut.isPending
  const allSelected =
    filteredCategories.length > 0 && selectedCategories.length === filteredCategories.length

  const handleRequestSimulationAllStores = (data: {
    emails: string[]
    sendToPilotage: boolean
  }) => {
    if (data.emails.length === 0 && !data.sendToPilotage) {
      toast.error('Veuillez renseigner au moins un email ou cocher le pilotage Lucie.')
      return
    }

    requestSimulationAllStoresByEmail(data, {
      onError: (error) => {
        let message =
          'Impossible de lancer la génération de la simulation Excel dans toutes les boutiques.'
        if (
          error instanceof AxiosError &&
          error.response?.data &&
          typeof error.response.data === 'object' &&
          'message' in error.response.data
        ) {
          message = error.response.data.message as string
        }
        toast.error(message)
      },
      onSuccess: () => {
        toast.success('La simulation est en cours de génération.', {
          description: 'Vous recevrez un email avec le fichier dans quelques minutes.',
        })
        setSimulationDialogOpen(false)
      },
    })
  }

  const tableBody = () => {
    if (isLoading || isPending) {
      return (
        <tr>
          <td colSpan={8} className="px-4 py-8 text-center text-sm text-muted-foreground">
            Chargement...
          </td>
        </tr>
      )
    }
    if (filteredCategories.length === 0) {
      return (
        <tr>
          <td colSpan={8} className="px-4 py-8 text-center text-sm text-muted-foreground">
            Aucune catégorie trouvée
          </td>
        </tr>
      )
    }
    return filteredCategories.map((category) => {
      const isSelected = selectedCategories.includes(category.id)
      return (
        <tr
          key={category.id}
          className={`border-b border-border/50 cursor-pointer transition-colors hover:bg-muted/30 ${isSelected ? 'bg-primary/5' : ''}`}
          onClick={() => toggleSelectCategory(category.id)}
        >
          <td className="px-4 py-3">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => toggleSelectCategory(category.id)}
              onClick={(e) => e.stopPropagation()}
              className="w-4 h-4 rounded accent-primary"
            />
          </td>
          <td className="px-4 py-3 text-sm">{category.name}</td>
          <td className="px-4 py-3 text-sm">{category.daysBefore}</td>
          <td className="px-4 py-3 text-sm">{formatCoverageRate(category.coverageRate)}</td>
          <td className="px-4 py-3 text-sm">{formatHour(category.computeHour)}</td>
          <td className="px-4 py-3 text-sm">{formatHour(category.sendHour)}</td>
          <td className="px-4 py-3 text-sm">{formatHour(category.logHour)}</td>
          <td className="px-4 py-3 text-sm">
            {category.isDispatch ? (
              <Check size={16} className="text-green-600" />
            ) : (
              <X size={16} className="text-red-500" />
            )}
          </td>
        </tr>
      )
    })
  }

  return (
    <>
      {/* Toolbar */}
      <div className="flex items-center justify-between p-2">
        <div className="flex items-center border border-border rounded-lg px-3 py-2 w-64 bg-background">
          <Search size={16} className="text-muted-foreground mr-2 shrink-0" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Recherche"
            className="flex-1 text-sm outline-none bg-transparent"
          />
        </div>

        <div className="flex items-center gap-2">
          {isUserAdmin && (
            <Button
              variant="destructive"
              onClick={() => setDeleteConfirmOpen(true)}
              disabled={deleteDisabled}
            >
              <Trash2 />
              Supprimer
            </Button>
          )}
          <Button
            variant="outline"
            disabled={modifyDisabled}
            onClick={() => setEditDialogOpen(true)}
          >
            {isUserAdmin ? (
              <>
                <Pencil />
                Modifier
              </>
            ) : (
              <>
                <Eye />
                Voir
              </>
            )}
          </Button>
          <Button
            variant="outline"
            disabled={!isUserAdmin}
            onClick={() => navigate({ to: '/categories/reappro/config' })}
          >
            <Settings />
            Configurer
          </Button>
          <Button
            variant="outline"
            disabled={!isUserAdmin}
            onClick={() => setStockMinDialogOpen(true)}
          >
            <FileSpreadsheet />
            Stocks min fixes
          </Button>
          <Button
            variant="outline"
            disabled={!isUserAdmin}
            onClick={() => setReapproImportDialogOpen(true)}
          >
            <Upload />
            Import modifications
          </Button>
          <Button
            disabled={!isUserAdmin || isRequestingSimulationAllStores}
            className="bg-[#FF9F00] hover:bg-[#e68f00] text-white"
            onClick={() => setSimulationDialogOpen(true)}
          >
            <FileSpreadsheet />
            {isRequestingSimulationAllStores ? 'Génération...' : 'Simulation générale'}
          </Button>
          <Button onClick={() => setAddDialogOpen(true)}>
            <Plus />
            Ajouter
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="mx-2 bg-background rounded-xl border border-border overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-muted/50 border-b border-border">
              <th className="w-12 px-4 py-3">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleSelectAll}
                  className="w-4 h-4 rounded accent-primary"
                />
              </th>
              <th className="text-left px-4 py-3 text-xs text-muted-foreground uppercase tracking-wider">
                Nom
              </th>
              <th className="text-left px-4 py-3 text-xs text-muted-foreground uppercase tracking-wider">
                J-X
              </th>
              <th className="text-left px-4 py-3 text-xs text-muted-foreground uppercase tracking-wider">
                Taux couverture
              </th>
              <th className="text-left px-4 py-3 text-xs text-muted-foreground uppercase tracking-wider">
                Heure de calcul
              </th>
              <th className="text-left px-4 py-3 text-xs text-muted-foreground uppercase tracking-wider">
                Heure d'envoi
              </th>
              <th className="text-left px-4 py-3 text-xs text-muted-foreground uppercase tracking-wider">
                Heure de log
              </th>
              <th className="text-left px-4 py-3 text-xs text-muted-foreground uppercase tracking-wider">
                Dispatch
              </th>
            </tr>
          </thead>
          <tbody>{tableBody()}</tbody>
        </table>
      </div>

      {/* Algorithm formulas reference */}
      <div className="mx-2 mt-4">
        <div className="flex items-center gap-2 mb-3">
          <Calculator size={14} className="text-primary" />
          <span className="text-sm text-muted-foreground">Formules des algorithmes</span>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <FormulaCard
            label="VMAX"
            isActive
            color="#15803d"
            formulaText="Besoin = (VMAX × TauxCouv − StockActuel + StockDLC − StockArr) × CoefSécu × CoefWE"
          />
          <FormulaCard
            label="VMJ"
            isActive
            color="#2563eb"
            formulaText="Besoin = (VMJ × TauxCouv − StockActuel + StockDLC − StockArr) × CoefSécu × CoefWE"
          />
          <FormulaCard
            label="Stock Projeté"
            isActive
            color="#7c3aed"
            formulaText="Besoin = (Prévision × CoefSécu) − StockProjeté"
          />
        </div>
      </div>

      {/* Algorithm params accordion */}
      {/* <ReapproAlgorithmParamsPanel isUserAdmin={isUserAdmin} /> */}

      {/* Delete confirmation dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Supprimer ?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Êtes-vous sûr de vouloir supprimer{' '}
            {selectedCategories.length === 1 ? 'cette catégorie' : 'ces catégories'} ?
          </p>
          <DialogFooter>
            <DialogClose render={<Button variant="outline" />}>Annuler</DialogClose>
            <Button
              variant="destructive"
              onClick={handleDeleteCategory}
              disabled={deleteCategoryMut.isPending}
            >
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ReapproStockMinBulkDialog open={stockMinDialogOpen} onOpenChange={setStockMinDialogOpen} />

      <ReapproNeedBulkImportDialog
        open={reapproImportDialogOpen}
        onOpenChange={setReapproImportDialogOpen}
      />

      <ReapproSimulationEmailDialog
        open={simulationDialogOpen}
        onOpenChange={setSimulationDialogOpen}
        isPending={isRequestingSimulationAllStores}
        onSubmit={handleRequestSimulationAllStores}
      />

      {/* Edit dialog */}
      <ReapproCategoryDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        reapproCategoryId={editDialogOpen ? selectedCategories[0] : undefined}
        onSuccess={() => {
          refetch()
          setSelectedCategories([])
        }}
      />

      {/* Add dialog */}
      <ReapproCategoryDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        onSuccess={refetch}
      />
    </>
  )
}
